import { getOrganisations, getTwinById } from '@repo/front-end-services';
import type { AdminTwin } from '@repo/frontend-types';

/**
 * Which dGraph namespace should this twin be activated into?
 *
 * The wizard used to answer this with `authMetadata.orgBID` — the org the *operator*
 * has selected — and never checked it against the org the *twin* belongs to. A
 * `TL_ADMIN` sees every twin across every org and the wizard's wrench is rendered on
 * all of them, so a mismatch was the default case, not an edge case. Observed live on
 * staging: with Newport Live selected, Go Live on Demo Hotel read from `NPLV2` (where
 * Demo Hotel does not exist), took "absent" to mean "first activation", **skipped
 * versioning so no backup was taken**, and wrote Demo Hotel's plan into another
 * tenant's namespace — reporting success. On a twin whose org *is* selected, that same
 * path destroys a live model with no backup.
 *
 * The R2 delta guard cannot catch it: pointed at the wrong namespace it correctly sees
 * "nothing is live" and correctly waves the run through. The guard is sound; its input
 * was not. So the input is resolved from the twin here.
 *
 * Resolution is `twin.organisation_id` → `organisation.bid`, both from AdminDB. It is
 * deliberately NOT parsed out of the twin BID — the prefix is not the org BID
 * (`NPL-STN` belongs to org `NPLV2`), so string-slicing would silently produce a
 * plausible-looking wrong namespace, which is worse than the bug being fixed.
 */
export type ActivationOrgOutcome =
  /**
   * Resolved, and it agrees with the operator's selected org.
   *
   * `twin` is the AdminDB row this resolution already had to read. It is returned rather than
   * discarded so Go Live's origin resolution (`resolveActivationOrigin`) can read the twin's
   * latitude/longitude from the SAME fetch that settled the namespace — one round trip, and no
   * window in which the two could disagree about which twin they are talking about.
   */
  | { status: 'ok'; orgBid: string; twin: AdminTwin }
  /**
   * Resolved, but the twin belongs to a different org than the one the operator has
   * selected. Refused rather than silently activating cross-context: a mismatch means
   * the operator is not looking at what they think they are.
   */
  | { status: 'mismatch'; twinOrgBid: string; selectedOrgBid: string }
  /**
   * Could not resolve. Both lookups swallow their errors and return null/undefined, so
   * "lookup failed" and "no such twin/org" are indistinguishable here — and neither may
   * fall back to the operator's org. Failing closed is the entire point.
   */
  | { status: 'failed'; reason: string };

export interface ResolveActivationOrgParams {
  middlewareApi: string;
  accessToken: string;
  /** The twin being activated, from the wizard's `?twinBid=`. */
  twinBid: string;
  /** The org the operator currently has selected (`authMetadata.orgBID`). */
  selectedOrgBid: string;
  /** Bounds for the two reads this makes. Additive; Go Live's existing call passes none. */
  options?: { timeout?: number; signal?: AbortSignal };
}

export const resolveActivationOrg = async ({
  middlewareApi,
  accessToken,
  twinBid,
  selectedOrgBid,
  options,
}: ResolveActivationOrgParams): Promise<ActivationOrgOutcome> => {
  const twin = await getTwinById(middlewareApi, accessToken, twinBid, options);
  if (!twin) {
    return { status: 'failed', reason: `Could not read the twin record for ${twinBid}.` };
  }
  if (twin.organisation_id == null) {
    return { status: 'failed', reason: `Twin ${twinBid} has no organisation on its record.` };
  }

  // No by-numeric-id org endpoint exists on the client; the list carries `{ id, bid }`,
  // which is exactly the mapping needed. If the list is scoped to fewer orgs than the
  // caller can see, the lookup misses and we fail closed rather than guessing.
  const organisations = await getOrganisations(middlewareApi, accessToken, options);
  if (!organisations) {
    return { status: 'failed', reason: 'Could not read the organisation list.' };
  }

  const org = organisations.find(o => o.id === twin.organisation_id);
  if (!org?.bid) {
    return {
      status: 'failed',
      reason: `Organisation ${twin.organisation_id} (the owner of ${twinBid}) could not be resolved to a BID.`,
    };
  }

  if (org.bid !== selectedOrgBid) {
    return { status: 'mismatch', twinOrgBid: org.bid, selectedOrgBid };
  }

  return { status: 'ok', orgBid: org.bid, twin };
};

/** User-facing copy for a refused activation. Kept here so the tests pin the wording. */
export const activationOrgErrorMessage = (outcome: ActivationOrgOutcome): string | null => {
  if (outcome.status === 'ok') return null;
  if (outcome.status === 'mismatch') {
    return (
      `This twin belongs to ${outcome.twinOrgBid}, but you have ${outcome.selectedOrgBid} selected. ` +
      'Activation was stopped before anything was changed — switch to the twin’s organisation ' +
      'and try again.'
    );
  }
  return (
    'Could not confirm which organisation this twin belongs to, so activation was stopped ' +
    'before anything was changed. Please try again.'
  );
};
